import { lazy, Suspense, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import type { ClipPlayback } from '@proref/types';
import { useClipQuery } from '../../hooks/use-clips';
import { useSportsQuery } from '../../hooks/use-sports';
import { useFederationsQuery } from '../../hooks/use-federations';
import { Button } from '../../components/shared/Button';
import { Icon } from '../../components/shared/Icons';
import { Spinner } from '../../components/shared/Spinner';
import { ListSkeleton } from '../../components/shared/Skeleton';
import { DiscussionThread } from '../../components/discussion/DiscussionThread';
import { apiErrorMessage } from '../../lib/api';
import { DeleteClipDialog } from './DeleteClipDialog';
import { UnpublishClipDialog } from './UnpublishClipDialog';
import { deriveRowState, statusLabel, STATUS_CHIP } from './ClipsPage';

// Lazy — Video.js is ~600 KB and only needed on clip surfaces, never on the console landing.
const VideoPlayer = lazy(() =>
  import('../../components/shared/VideoPlayer').then((m) => ({ default: m.VideoPlayer })),
);

// The super_admin clip surface: a read-only review of one clip with its video + metadata AND its
// discussion shown immediately (no answer gate — the backend grants staff access, so we pass
// unlocked). Management (edit / unpublish / delete) is available here rather than on the list.
// Reachable by any admin (route guard), but linked from the super_admin console.
export function ClipReviewPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const clipQuery = useClipQuery(id);

  if (clipQuery.isLoading) {
    return (
      <div className="flex flex-col gap-4">
        <ListSkeleton rows={2} />
      </div>
    );
  }
  if (clipQuery.isError || !clipQuery.data) {
    return (
      <div className="rounded border border-danger/40 bg-danger/10 px-4 py-3 text-[13px] text-danger">
        {apiErrorMessage(clipQuery.error, t('admin.clips.review.couldNotLoad'))}
      </div>
    );
  }

  return <ClipReview clip={clipQuery.data} onBack={() => navigate(`/admin/clips?sport=${clipQuery.data!.sport_id}`)} />;
}

function ClipReview({ clip, onBack }: { clip: ClipPlayback; onBack: () => void }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const sportsQuery = useSportsQuery();
  const federationsQuery = useFederationsQuery();
  const [dialog, setDialog] = useState<'delete' | 'unpublish' | null>(null);

  const sportName = sportsQuery.data?.find((s) => s.id === clip.sport_id)?.name ?? '—';
  const federationName = federationsQuery.data?.find((f) => f.id === clip.federation_id)?.name ?? '—';
  const state = deriveRowState(clip);
  const isPublished = state === 'published';
  const inFlight = state === 'uploading' || state === 'processing';

  return (
    <>
      {/* Header — back to the sport's clip list, title, and management actions. */}
      <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <button
            type="button"
            onClick={onBack}
            className="mb-1.5 inline-flex items-center gap-1 text-[12.5px] text-muted hover:text-ink"
          >
            <Icon.chevronLeft className="h-3.5 w-3.5" /> {t('admin.clips.review.backToClips')}
          </button>
          <div className="flex items-center gap-2.5">
            <h1 className="truncate text-[19px] font-semibold text-ink">{clip.title}</h1>
            <span
              className={
                'inline-flex h-[22px] flex-none items-center gap-1.5 rounded-full px-2.5 text-[11px] font-medium ' +
                STATUS_CHIP[state]
              }
            >
              {inFlight && <Spinner className="w-2.5 h-2.5" />}
              {statusLabel(t, state)}
            </span>
          </div>
          <p className="mt-0.5 text-[12.5px] text-muted">
            {sportName} · {federationName}
          </p>
        </div>

        <div className="flex flex-none items-center gap-2">
          <Button variant="default" onClick={() => navigate(`/admin/clips/${clip.id}/edit`)}>
            <Icon.pencil className="h-3.5 w-3.5" /> {t('admin.clips.review.edit')}
          </Button>
          {isPublished && (
            <Button variant="default" onClick={() => setDialog('unpublish')}>
              <Icon.eyeOff className="h-3.5 w-3.5" /> {t('admin.clips.review.unpublish')}
            </Button>
          )}
          <Button
            variant="danger"
            onClick={() => setDialog('delete')}
            disabled={isPublished || inFlight}
            title={
              isPublished
                ? t('admin.clips.review.deleteBlockedPublished')
                : inFlight
                  ? t('admin.clips.review.deleteBlockedInFlight')
                  : undefined
            }
          >
            <Icon.trash className="h-3.5 w-3.5" /> {t('admin.clips.review.delete')}
          </Button>
        </div>
      </div>

      <div className="grid gap-5 lg:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)]">
        {/* Left: video + metadata */}
        <div className="flex flex-col gap-4">
          {clip.playback_url ? (
            <Suspense
              fallback={<div className="aspect-video w-full animate-pulse rounded-md border border-line-2 bg-bg-2" />}
            >
              <VideoPlayer
                src={clip.playback_url}
                poster={clip.poster_url ?? undefined}
                className="overflow-hidden rounded-md border border-line-2 bg-ink"
              />
            </Suspense>
          ) : (
            // No signed URL (still transcoding, or no CloudFront locally) — show the poster or a
            // neutral placeholder so the layout holds and the discussion is still reviewable.
            <div className="relative aspect-video w-full overflow-hidden rounded-md border border-line-2 bg-bg-2">
              {clip.poster_url ? (
                <img src={clip.poster_url} alt="" className="h-full w-full object-cover" />
              ) : (
                <div className="grid h-full w-full place-items-center text-[12px] uppercase tracking-wider text-muted">
                  {inFlight ? t('admin.clips.review.transcoding') : t('admin.clips.review.noPreview')}
                </div>
              )}
            </div>
          )}

          {clip.description && (
            <p className="text-[13px] leading-relaxed text-ink-2">{clip.description}</p>
          )}

          <dl className="grid grid-cols-2 gap-x-4 gap-y-3 rounded border border-line bg-surface p-4 shadow-1">
            <MetaItem label={t('admin.clips.review.ruleLabel')} value={clip.rule_article ? `${clip.rule_article.article_number} · ${clip.rule_article.title}` : t('common.untagged')} />
            <MetaItem label={t('admin.clips.review.sportLabel')} value={sportName} />
            <MetaItem label={t('admin.clips.review.federationLabel')} value={federationName} />
            <MetaItem label={t('admin.clips.review.uploadedByLabel')} value={clip.uploaded_by_user?.full_name ?? '—'} />
            <MetaItem label={t('admin.clips.review.durationLabel')} value={clip.duration_sec ? formatDuration(clip.duration_sec) : '—'} />
            <MetaItem label={t('admin.clips.review.decisionPointLabel')} value={clip.decision_timestamp_ms != null ? formatTimecode(clip.decision_timestamp_ms / 1000) : '—'} />
          </dl>
        </div>

        {/* Right: discussion — shown immediately (staff access, no answer gate). */}
        <div className="min-w-0">
          <DiscussionThread clipId={clip.id} unlocked />
        </div>
      </div>

      {dialog === 'unpublish' && (
        <UnpublishClipDialog open onClose={() => setDialog(null)} clipId={clip.id} clipTitle={clip.title} />
      )}
      {dialog === 'delete' && (
        <DeleteClipDialog
          open
          onClose={() => setDialog(null)}
          clipId={clip.id}
          clipTitle={clip.title}
          onDeleted={onBack}
        />
      )}
    </>
  );
}

function MetaItem({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0">
      <dt className="text-[11px] font-medium uppercase tracking-[0.05em] text-muted">{label}</dt>
      <dd className="mt-0.5 truncate text-[13px] text-ink">{value}</dd>
    </div>
  );
}

function formatDuration(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = Math.round(sec % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function formatTimecode(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  const cs = Math.round((sec - Math.floor(sec)) * 100);
  return `${m}:${s.toString().padStart(2, '0')}.${cs.toString().padStart(2, '0')}`;
}
